import React, { useState } from 'react';
import { 
  StopIcon, 
  EyeIcon, 
  ChevronDownIcon, 
  ChevronUpIcon,
  ClockIcon,
  UserIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { Button } from '../ui';
import processService from '../../services/processService';

const ProcessCard = ({ process, onCancel, showDetails = false }) => {
  const [showLogs, setShowLogs] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const statusInfo = processService.getStatusDisplayInfo(process.status);
  const typeIcon = processService.getProcessTypeIcon(process.process_type);
  const typeName = processService.getProcessTypeDisplayName(process.process_type);
  const progressPercentage = processService.calculateProgressPercentage(process.progress);
  const progressColor = processService.getProgressColor(progressPercentage);
  const canCancel = processService.canCancelProcess(process.status);
  const isStuck = processService.isProcessStuck(process.updated_at);
  const lastLogMessage = processService.getLastLogMessage(process.logs);

  const handleCancel = () => {
    if (onCancel && canCancel) {
      onCancel(process.id);
    }
  };

  const getProgressBarColor = () => {
    switch (progressColor) {
      case 'green': return 'bg-green-500';
      case 'blue': return 'bg-blue-500';
      case 'yellow': return 'bg-yellow-500';
      case 'red': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadgeColor = () => {
    switch (statusInfo.color) {
      case 'green': return 'bg-green-100 text-green-800';
      case 'blue': return 'bg-blue-100 text-blue-800';
      case 'yellow': return 'bg-yellow-100 text-yellow-800';
      case 'red': return 'bg-red-100 text-red-800';
      case 'gray': return 'bg-gray-100 text-gray-800';
      case 'orange': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border ${isStuck ? 'border-red-200' : 'border-gray-200'} overflow-hidden`}>
      {/* Header */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className="text-lg">{typeIcon}</span>
            <h3 className="text-lg font-medium text-gray-900">{typeName}</h3>
            {isStuck && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                ⚠️ Stuck
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor()}`}>
              {statusInfo.icon} {statusInfo.text}
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-3">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Progress</span>
            <span>{progressPercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${getProgressBarColor()}`}
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>

        {/* Progress Stats */}
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {process.progress.processed.toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">Processed</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {process.progress.total.toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">Total</div>
          </div>
        </div>

        {/* Success/Failure Stats */}
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div className="text-center">
            <div className="text-lg font-semibold text-green-600">
              {process.progress.successful.toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">Success</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-red-600">
              {process.progress.failed.toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">Failed</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-2">
          <Button
            variant="secondary"
            size="sm"
            icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Less' : 'More'}
          </Button>
          
          {canCancel && (
            <Button
              variant="danger"
              size="sm"
              icon={StopIcon}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
          <div className="space-y-3">
            {/* Process Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Process ID:</span>
                <div className="text-gray-600 font-mono text-xs">{process.id}</div>
              </div>
              <div>
                <span className="font-medium text-gray-700">Duration:</span>
                <div className="text-gray-600">{processService.formatDuration(process.duration)}</div>
              </div>
            </div>

            {/* Timestamps */}
            <div className="grid grid-cols-1 gap-2 text-sm">
              <div className="flex items-center space-x-2">
                <CalendarIcon className="h-4 w-4 text-gray-400" />
                <span className="font-medium text-gray-700">Started:</span>
                <span className="text-gray-600">{processService.formatTimestamp(process.started_at)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <ClockIcon className="h-4 w-4 text-gray-400" />
                <span className="font-medium text-gray-700">Last Update:</span>
                <span className="text-gray-600">{processService.getTimeAgo(process.updated_at)}</span>
              </div>
            </div>

            {/* Account Info */}
            {process.account_name && (
              <div className="flex items-center space-x-2 text-sm">
                <UserIcon className="h-4 w-4 text-gray-400" />
                <span className="font-medium text-gray-700">Account:</span>
                <span className="text-gray-600">{process.account_name}</span>
              </div>
            )}

            {/* Success/Failure Rates */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Success Rate:</span>
                <div className="text-green-600">{processService.getSuccessRate(process.progress)}%</div>
              </div>
              <div>
                <span className="font-medium text-gray-700">Failure Rate:</span>
                <div className="text-red-600">{processService.getFailureRate(process.progress)}%</div>
              </div>
            </div>

            {/* Last Log Message */}
            <div className="text-sm">
              <span className="font-medium text-gray-700">Last Log:</span>
              <div className="text-gray-600 mt-1 p-2 bg-gray-100 rounded text-xs">
                {lastLogMessage}
              </div>
            </div>

            {/* Show Logs Button */}
            {process.logs && process.logs.length > 0 && (
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={EyeIcon}
                  onClick={() => setShowLogs(!showLogs)}
                >
                  {showLogs ? 'Hide Logs' : `Show Logs (${process.logs.length})`}
                </Button>

                {showLogs && (
                  <div className="mt-3 bg-gray-900 text-gray-100 p-3 rounded text-xs font-mono max-h-60 overflow-y-auto">
                    {process.logs.map((log, index) => (
                      <div key={index} className="mb-2">
                        <span className="text-gray-400">{new Date(log.time).toLocaleTimeString()}</span>
                        <span className="ml-2">{log.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessCard;
